import { IComboBoxOption, mergeStyles, Stack } from '@fluentui/react';
import { Button, buttonStylesLink, Item, itemStylesTag, useTheme } from '@h2oai/ui-kit';
import { memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { FiltersSelect } from './components/FiltersSelect';
import { filtersStyles } from './styles';
import { PageFiltersProps, PageFilterValue } from './types';

export const PageFilters = memo((props: PageFiltersProps) => {
  const { title = 'Filters', filters = [], filterValues = {}, onFiltersChange, onClearFilters, onClearFilter } = props;
  const theme = useTheme();

  const filterOptions: IComboBoxOption[] = useMemo(() => {
    return filters.map((filter) => ({
      key: filter.key,
      text: filter.filterName,
      disabled: filter.disabled,
      data: { value: filter.state?.value },
    }));
  }, [filters]);

  const [activeFilterKey, setActiveFilterKey] = useState<string | undefined>(
    filters.length === 1 ? filters[0].key : undefined
  );

  const activeFilter = useMemo(() => {
    if (filters.length === 1) return filters;
    if (!activeFilterKey) return [];
    return filters.filter((filter) => filter.key === activeFilterKey);
  }, [filters, activeFilterKey]);

  const handleChange = useCallback(
    (key: string) => (value: PageFilterValue, readableValue?: string) => {
      if (onFiltersChange) {
        const newFilterValues = {
          ...filterValues,
          [key]: { value, readableValue },
        };
        onFiltersChange(newFilterValues);
      }
    },
    [onFiltersChange, filterValues]
  );

  const handleActiveFilterChange = useCallback((filterKey?: string) => {
    setActiveFilterKey(filterKey);
  }, []);

  const handleClearFilters = useCallback(() => {
    if (onClearFilters) onClearFilters();
    setActiveFilterKey(undefined);
  }, [onClearFilters]);

  const handleClearActiveFilter = useCallback(() => {
    if (activeFilterKey && onClearFilter) onClearFilter(activeFilterKey);
  }, [activeFilterKey, onClearFilter]);

  const activeFilterLayout = useMemo(
    () => (
      <Stack horizontal verticalAlign="end">
        {activeFilter.map((filter) => (
          <Stack key={filter.key}>
            {filter.render(
              filter.state?.value,
              handleChange(filter.key),
              filter.inputLabel,
              filter.key,
              filter.disabled
            )}
          </Stack>
        ))}
        {activeFilter.length > 0 && !!activeFilter[0].state?.value && (
          <Stack styles={{ root: { marginLeft: 8 } }}>
            <Button iconName="ChromeClose" onClick={handleClearActiveFilter} styles={buttonStylesLink}>
              Reset filter
            </Button>
          </Stack>
        )}
      </Stack>
    ),
    [activeFilter, handleChange, handleClearActiveFilter]
  );

  useEffect(() => {
    if (filters.length > 1 && (activeFilterKey === undefined || !filters.some((f) => f.key === activeFilterKey))) {
      const firstSet = filters.find((f) => {
        const val = f.state?.value;
        return val !== undefined && val !== null && val !== '';
      });
      if (firstSet) {
        setActiveFilterKey(firstSet.key);
      }
    }
  }, [filters, activeFilterKey]);

  const filterChips = useMemo(() => {
    const chips: ReactNode[] = [];

    filters.forEach((filter) => {
      const filterState = filter.state;
      if (filterState?.value !== undefined && filterState?.value !== null && filterState?.value !== '') {
        const readableValue = filterState.readableValue;

        if (readableValue) {
          chips.push(
            <Stack key={filter.key} horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
              <Item
                data={{ title: readableValue }}
                labelField="title"
                styles={[
                  itemStylesTag,
                  {
                    root: {
                      backgroundColor: theme.palette?.gray200,
                    },
                    labelText: {
                      color: theme.palette?.gray900,
                    },
                  },
                ]}
              />
              <Button
                iconName="ChromeClose"
                styles={buttonStylesLink}
                onClick={() => onClearFilter?.(filter.key)}
                title={`Clear ${filter.filterName} filter`}
              />
            </Stack>
          );
        }
      }
    });

    return chips;
  }, [filters, theme, onClearFilter]);

  const fieldsetClassNames = mergeStyles(filtersStyles(theme));

  return (
    <fieldset className={fieldsetClassNames}>
      <legend>{title}</legend>

      <Stack horizontal horizontalAlign="space-between" verticalAlign="end" styles={{ root: { width: '100%' } }}>
        <Stack horizontal horizontalAlign="center" tokens={{ childrenGap: 16 }}>
          {filters.length === 0 ? (
            <Stack key="no-filters">No Filters provided</Stack>
          ) : filters.length === 1 ? (
            activeFilterLayout
          ) : (
            <Stack key="filters-with-select" horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              <FiltersSelect
                options={filterOptions}
                onChange={handleActiveFilterChange}
                activeFilter={activeFilterKey}
              />

              {activeFilterLayout}
            </Stack>
          )}
        </Stack>

        {filters.length > 0 && (
          <Button iconName="ClearFilter" onClick={handleClearFilters}>
            Clear filters
          </Button>
        )}
      </Stack>

      {filterChips.length > 0 && (
        <Stack horizontal wrap tokens={{ childrenGap: 4 }}>
          {filterChips}
        </Stack>
      )}
    </fieldset>
  );
});
